<template>
  <FormulateForm @input="performValidation" @submit="payNow" ref="form">
    <div class="bg-primary-500">
      <div class="pt-12 sm:pt-16 lg:pt-20">
        <div class="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center">
            <h2
              class="text-3xl leading-9 font-light text-white sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none"
            >
              <a :href="`/${urlAlias}`" class="whitespace-nowrap">
                {{ nickname }}
              </a>
            </h2>
            <p class="mt-4 text-xl leading-7 text-gray-50 font-extralight">
              {{ $t('accessContent, { buyerName }!', { buyerName }) }}
            </p>
          </div>
        </div>
      </div>
      <div class="mt-4 bg-secondary-500 pb-16 sm:mt-4 sm:pb-20 lg:pb-28">
        <div class="relative">
          <div class="absolute inset-0 h-1/2 bg-primary-500"></div>
          <div
            class="relative max-w-screen-xl pt-9 lg:pt-24 mx-auto px-4 sm:px-6 lg:px-20"
          >
            <div class="absolute inset-0 pointer-events-none">
              <div
                class="relative w-16 h-16 lg:h-48 lg:w-48 rounded-full border-white border-2 lg:border-4 mx-auto overflow-hidden"
              >
                <img
                  v-if="avatar"
                  :src="`${avatar}`"
                  class="absolute left-0 top-0 w-full h-full object-cover"
                />
              </div>
            </div>

            <div
              class="max-w-lg mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:flex"
            >
              <div class="flex-1 bg-white px-6 py-8 lg:p-12">
                <div
                  class="sm:-mt-6 sm:-ml-6 mb-6 text-base leading-6 text-secondary-300"
                >
                  <router-link
                    :to="{ name: 'index', params: { rewardId, buyerId } }"
                    class="whitespace-nowrap flex align-center"
                  >
                    <ArrowLeftIcon class="w-5 mr-1" />
                    <span class="text-primary-600">
                      {{ $t('Payment method selection') }}
                    </span>
                  </router-link>
                </div>

                <h3
                  v-if="$route.name === 'sepa'"
                  class="text-2xl leading-8 font-medium text-gray-900 sm:text-3xl sm:leading-9 lg:w-1/2"
                >
                  {{ $t('SEPA direct debit') }}
                </h3>

                <h3
                  v-if="$route.name === 'card'"
                  class="text-2xl leading-8 font-medium text-gray-900 sm:text-3xl sm:leading-9 lg:w-1/2"
                >
                  {{ $t('Pay by credit card') }}
                </h3>

                <div class="mt-8">
                  <div class="space-y-6">
                    <FormulateInput
                      :label="$t('Email')"
                      type="email"
                      v-model="accountholderEmail"
                      :validation="`required|email`"
                    />

                    <div v-if="$route.name === 'sepa'">
                      <label
                        class="block text-sm font-medium leading-5 text-gray-500"
                      >
                        IBAN
                      </label>
                      <div class="mt-1 block w-full rounded-lg shadow-sm">
                        <div
                          class="block w-full px-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 sm:text-sm sm:leading-5"
                        >
                          <div id="iban-element">
                            <!-- A Stripe Element will be inserted here. -->
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="ibanError"
                        class="text-sm font-medium mt-1 mb-1 text-primary-500"
                      >
                        {{ ibanError }}
                      </div>
                    </div>
                    <div v-if="$route.name === 'sepa'">
                      <FormulateInput
                        :label="$t('Account holder')"
                        type="text"
                        v-model="accountholderName"
                        :validation="`required`"
                        class="py-1"
                      />
                      <div
                        v-if="regulatedSepaCode"
                        class="grid gap-4 pt-4 md:grid-cols-2"
                      >
                        <FormulateInput
                          :label="$t('Billing line')"
                          type="text"
                          v-model="billingLine1"
                          :validation="`required`"
                          class="py-1"
                        />
                        <FormulateInput
                          :label="$t('Billing postal code')"
                          type="text"
                          v-model="billingPostalCode"
                          :validation="`required`"
                          class="py-1"
                        />
                        <FormulateInput
                          :label="$t('Billing city')"
                          type="text"
                          v-model="billingCity"
                          :validation="`required`"
                          class="py-1"
                        />
                        <!-- {{ billingCountry }} -->
                        <FormulateInput
                          :label="$t('Billing country')"
                          type="select"
                          validation="required"
                          v-model="billingCountry"
                          :options="sepaCountries[$i18n.locale]"
                          class="py-1"
                        />
                      </div>
                    </div>

                    <div v-if="$route.name === 'card'">
                      <label
                        class="block text-sm font-medium leading-5 text-gray-500"
                      >
                        {{ $t('Card details') }}
                      </label>
                      <div class="mt-1 block w-full rounded-lg shadow-sm">
                        <div
                          class="block w-full px-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 sm:text-sm sm:leading-5"
                        >
                          <div id="card-element">
                            <!-- A Stripe Element will be inserted here. -->
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="cardError"
                        class="text-sm font-medium mt-1 mb-1 text-primary-500"
                      >
                        {{ cardError }}
                      </div>
                    </div>
                    <FormulateInput
                      v-if="$route.name === 'card'"
                      :label="$t('Cardholder name')"
                      type="text"
                      v-model="accountholderName"
                      :validation="`required`"
                    />

                    <div class="pt-4 pb-4">
                      <span
                        v-if="active"
                        class="flex items-center justify-center px-5 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-gray-600 bg-gray-300"
                      >
                        {{ $t('Active subscription') }}
                      </span>

                      <div v-if="!active" class="flex items-center mb-5">
                        <withdrawal-waiver
                          :grantWithdrawalRight="item.grantWithdrawalRight"
                          @change="acceptedWaiver"
                        ></withdrawal-waiver>
                      </div>

                      <submitButton
                        v-if="!active"
                        :isLoading="isLoading"
                        :hasErrors="hasErrors || active"
                        :disabled="purchaseProhibited"
                      >
                        <template #submit>
                          {{ $t('Subscribe') }}
                        </template>
                        <template #loading>
                          {{ $t('...bezahlen') }}
                        </template>
                      </submitButton>
                    </div>
                  </div>
                  <!-- Display mandate acceptance text. -->
                  <div
                    v-if="$i18n.locale === 'en'"
                    class="mt-4 text-sm leading-5 font-normal text-gray-400 text-center"
                  >
                    By providing your payment information and confirming this
                    payment, you authorise (A) Fans &amp; Fortune GmbH and
                    Stripe, our payment service provider, to send instructions
                    to your bank to debit your account and (B) your bank to
                    debit your account in accordance with those instructions. As
                    part of your rights, you are entitled to a refund from your
                    bank under the terms and conditions of your agreement with
                    your bank. A refund must be claimed within 8 weeks starting
                    from the date on which your account was debited. Your rights
                    are explained in a statement that you can obtain from your
                    bank. You agree to receive notifications for future debits
                    up to 2 days before they occur.
                  </div>

                  <!-- Display mandate acceptance text. -->
                  <div
                    v-if="$i18n.locale === 'de'"
                    class="mt-4 text-sm leading-5 font-normal text-gray-400 text-center"
                  >
                    Durch Angabe der IBAN und Bestätigung der Zahlung ermächtige
                    ich/ermächtigen wir (A) Fans & Fortune GmbH und Stripe,
                    unseren Zahlungsdienstleister, Zahlungen von meinem/unserem
                    Konto mittels Lastschrift einzuziehen. Zugleich (B) weise
                    ich mein/weisen wir unser Kreditinstitut gemäß diesen
                    Anweisungen an, die auf mein/unser Konto gezogenen
                    Lastschriften einzulösen. Hinweis: Ich kann/Wir können
                    innerhalb von acht Wochen, beginnend mit dem
                    Belastungsdatum, die Erstattung des belasteten Betrages
                    verlangen. Es gelten dabei die mit meinem/unserem
                    Kreditinstitut vereinbarten Bedingungen.
                  </div>
                </div>
              </div>

              <!--- aside right --->

              <div
                class="py-8 px-6 text-center bg-secondary-100 alg:flex-shrink-0 lg:w-1/3 lg:flex lg:flex-col lg:justify-center lg:p-12"
              >
                <p
                  v-if="isRecurring"
                  class="text-lg leading-6 font-medium text-gray-900"
                >
                  <span v-if="!active">
                    {{ $t('Pay per month, cancel anytime') }}
                  </span>
                  <span v-else>
                    {{ $t('Active subscription, cancel anytime') }}
                  </span>
                </p>
                <p v-else class="text-lg leading-6 font-medium text-gray-900">
                  {{ $t('Pay once') }}
                </p>
                <div
                  class="mt-4 flex items-center justify-center text-5xl leading-none font-extrabold text-gray-900"
                >
                  <span v-if="!isCustom">
                    {{ $n(amount / 100, 'currency') }}
                  </span>
                  <span class="w-32" v-else>
                    <FormulateInput
                      type="number"
                      validation-name="Betrag"
                      error-behavior="live"
                      inputmode="numeric"
                      :validation="`number|min:${amount / 100}|max:${amount}`"
                      :show-value="true"
                      v-model="customAmount"
                      :min="amount / 100"
                    />
                  </span>
                  <span v-if="isCustom" class="font-normal text-lg pl-2">
                    ,- €
                  </span>
                  <span
                    v-if="isRecurring"
                    class="ml-3 text-xl leading-7 font-medium text-gray-500"
                  >
                    / {{ $t('month') }}
                  </span>
                  <span
                    v-else
                    class="ml-3 text-xl leading-7 font-medium text-gray-500"
                  >
                    / {{ $t('once') }}
                  </span>
                </div>
                <p class="mt-4 text-sm leading-5">
                  <span class="font-medium text-gray-500">
                    {{ $t('incl. 19% VAT') }}
                  </span>
                </p>

                <p
                  v-if="urlAlias && nickname"
                  class="mt-4 text-sm leading-5 font-normal text-gray-400"
                >
                  <a
                    :href="`/${urlAlias}/${$t('imprint').toLowerCase()}`"
                    target="_blank"
                  >
                    {{ nickname }} -
                    {{ $t('imprint') }}
                  </a>
                </p>
              </div>

              <!--- aside right --->
            </div>
          </div>
        </div>
      </div>
    </div>
  </FormulateForm>
</template>

<style scoped>
.btn {
  @apply w-full flex items-center justify-center px-5 py-3 border border-transparent text-base leading-6 font-medium rounded-md transition duration-150 ease-in-out;
}
</style>

<i18n>
en:
  Further details in the next step: You may provide further details in the next step
  Pay now: Pay now
  incl. 19% VAT: incl. 19% VAT
  once: once
  month: month
  Pay once: Pay once
  Pay per month, cancel anytime: Pay per month, cancel anytime
  Active subscription: Active subscription
  Active subscription, cancel anytime: Active subscription, cancel anytime
  ...loading: ...loading
  legal1: With the confirmation of the purchase I also agree to a waiver of revocation, as I will get immediate access to contents.
  Subscribe: Subscribe
  Cardholder name: Cardholder name
  Card details: Card details
  Account holder: Account holder
  Billing line: Street and number
  Billing city: City
  Billing postal code: Postal code
  Billing country: Country
  Email: Email
  Pay by credit card: Pay by credit card
  SEPA direct debit: SEPA direct debit
  Payment method selection: Payment method selection
  accessContent, { buyerName }!: Get immediate access to exclusive content, { buyerName }!
  imprint: Imprint

de:
  Further details in the next step: Weitere Angaben im nächsten Schritt
  Pay now: Jetzt kaufen
  incl. 19% VAT: inkl. 19% MwSt
  once: einmalig
  month: Monat
  Pay once: Zahle einmalig
  Pay per month, cancel anytime: Zahle monatlich, jederzeit kündbar
  Active subscription: Aktives Abo
  Active subscription, cancel anytime: Aktives Abo, jederzeit kündbar
  ...loading: ...laden
  legal1: Mit der Bestätigung des Kaufs stimme ich auch einem Verzicht auf Widerruf zu, da ich sofort Zugang zu Inhalten erhalte.
  Subscribe: Abonnieren
  Cardholder name: Name des/der Karteninhabers/-in
  Card details: Kartendaten
  Account holder: Kontoinhaber/-in
  Billing line: Straße und Hausnummer
  Billing postal code: Postleitzahl
  Billing city: Stadt
  Billing country: Land
  Email: E-Mail
  Pay by credit card: Kreditkarten Zahlung
  SEPA direct debit: SEPA Lastschriftverfahren
  Payment method selection: Auswahl Zahlungsart
  accessContent, { buyerName }!: Erhalte sofort Zugriff auf exklusive Inhalte, { buyerName }!
  imprint: Impressum
</i18n>

<script>
/* global Stripe */

import dottie from 'dottie'
import { store } from '@/store'
import { sync, call } from 'vuex-pathify'
import { ArrowLeftIcon } from '@vue-hero-icons/outline'

import submitButton from '@/components/submitButton.vue'

import WithdrawalWaiver from '@/components/withdrawalWaiver.vue'

const fetchData = async (to, from, next) => {
  const { rewardId, buyerId } = to.params
  await store.dispatch('reward/read', { rewardId, buyerId })
  await store.dispatch('user/read', buyerId)
  await store.dispatch('checkout/readKey', { rewardId })

  next()
}

const elementOptions = {
  style: {
    base: {
      color: '#32325d',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      },
      ':-webkit-autofill': {
        color: '#32325d'
      }
    },
    invalid: {
      color: '#FB3E52',
      iconColor: '#FB3E52',
      ':-webkit-autofill': {
        color: '#FB3E52'
      }
    }
  },
  supportedCountries: ['SEPA'],
  placeholderCountry: 'DE'
}

export default {
  beforeRouteEnter: fetchData,
  beforeRouteUpdate: fetchData,

  components: {
    ArrowLeftIcon,
    submitButton,
    WithdrawalWaiver
  },

  data() {
    return {
      $stripe: null,
      $elements: null,
      $iban: null,
      isMounted: false,
      ibanError: null,
      cardError: null,
      sessionId: null,
      hasErrors: false,
      isLoading: false,
      customAmount: null,
      accountholderName: null,
      accountholderEmail: null,
      billingLine1: null,
      billingPostalCode: null,
      billingCity: null,
      billingCountry: null,
      rewardId: null,
      buyerId: null,
      afterSaleAnswer: '',
      waiverAccepted: false
    }
  },

  computed: {
    buyer: sync('user'),
    buyerName: sync('user/nickname'),
    buyerEmail: sync('user/email'),
    sellerId: sync('reward/userId'),
    nickname: sync('reward/nickname'),
    urlAlias: sync('reward/urlAlias'),
    profile: sync('reward/profile'),
    reward: sync('reward/reward'),
    active: sync('reward/active'),
    product: sync('reward/product'),
    publishableKey: sync('checkout/publishableKey'),
    accId: sync('checkout/accId'),
    clientSecret: sync('checkout/clientSecret'),
    checkoutData: sync('checkout/checkoutData'),
    sepaCountries: sync('sepa/countries'),
    regulatedSepaCodes: sync('sepa/regulatedSepaCodes'),

    item() {
      return this.reward || this.product
    },
    amount() {
      return Math.max(this.item.amount || 0, this.item.minAmount || 0)
    },
    isCustom() {
      return this.item.minAmount && Number(this.item.minAmount) > 0
    },
    isRecurring() {
      return dottie.get(this.item, 'payment.recurring', false)
    },
    avatar() {
      return dottie.get(this.profile, 'imgs.avatar.previewUrl', null)
    },
    regulatedSepaCode() {
      return this.regulatedSepaCodes.includes(this.billingCountry)
    },
    purchaseProhibited() {
      return !this.item.grantWithdrawalRight && !this.waiverAccepted
    }
  },

  mounted() {
    this.customAmount = this.amount / 100

    this.$stripe = Stripe(this.publishableKey, {
      locale: this.$i18n.locale
    })

    this.$elements = this.$stripe.elements()

    if (this.$route.name === 'sepa') {
      // Create an instance of the IBAN Element
      this.$iban = this.$elements.create('iban', elementOptions)

      // Add an instance of the IBAN Element into the `iban-element` <div>
      this.$iban.mount('#iban-element')
      this.$iban.addEventListener('change', this.ibanChanged)
    }

    if (this.$route.name === 'card') {
      // Create an instance of the IBAN Element
      this.$card = this.$elements.create('card', elementOptions)

      // Add an instance of the card Element into the `card-element` <div>
      this.$card.mount('#card-element')
    }

    const { rewardId, buyerId } = this.$route.params

    this.rewardId = rewardId
    this.buyerId = buyerId

    this.checkoutData.itemId = rewardId
    this.checkoutData.buyerId = buyerId
    this.checkoutData.customAmount = this.customAmount

    this.accountholderEmail = this.buyerEmail

    this.$nextTick(() => {
      this.isMounted = true
    })

    if (window.fbq) {
      window.fbq('track', 'AddPaymentInfo')
    }
  },

  beforeDestroy() {
    if (this.$iban) this.$iban.removeEventListener('change', this.ibanChanged)
  },

  watch: {
    '$root.$i18n.locale'() {
      this.performValidation()
    }
  },

  methods: {
    startSepa: call('checkout/startSepa'),
    finishSepa: call('checkout/finishSepa'),

    ibanChanged(ibanStatus) {
      if (ibanStatus.country && ibanStatus.complete && !ibanStatus.error) {
        this.billingCountry = ibanStatus.country
      }
    },

    async performValidation() {
      if (this.isMounted && this.$refs.form) {
        this.hasErrors = await this.$refs.form.hasValidationErrors()
      }
    },

    async payNow() {
      if (this.hasErrors) return

      this.ibanError = null
      this.cardError = null
      this.isLoading = true

      const { rewardId, buyerId } = this.$route.params

      if (!this.clientSecret) {
        await this.startSepa({
          itemId: rewardId,
          buyerId: buyerId || this.buyer.userId
        })
      }

      const payload = {
        payment_method: {
          sepa_debit: this.$route.name === 'sepa' ? this.$iban : undefined,
          card: this.$route.name === 'card' ? this.$card : undefined,
          billing_details: {
            name: this.accountholderName,
            email: this.accountholderEmail
          }
        }
      }

      if (this.$route.name === 'sepa' && this.regulatedSepaCode) {
        payload.payment_method.billing_details.address = {
          line1: this.billingLine1,
          postal_code: this.billingPostalCode,
          city: this.billingCity,
          country: this.billingCountry
        }
      }

      const confirmSetup =
        this.$route.name === 'sepa'
          ? this.$stripe.confirmSepaDebitSetup
          : this.$stripe.confirmCardSetup

      const { error, setupIntent } = await confirmSetup(
        this.clientSecret,
        payload
      )

      if (error) {
        this.ibanError = error.message
        this.cardError = error.message
        this.isLoading = false
        return
      }

      if (setupIntent) {
        const finalize = {
          ...this.checkoutData,
          pmId: setupIntent.payment_method
        }

        await this.finishSepa(finalize)
        this.$router.push({
          name: 'successPageSepa',
          params: { sellerId: this.sellerId, rewardId: this.rewardId }
        })
      }

      this.isLoading = false
    },
    acceptedWaiver(val) {
      this.waiverAccepted = val
    }
  }
}
</script>
